import React, { useEffect, useState, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import "../PublicLayout/style.css";
import "./style.css";
import SidePanel from "./SidePanel";
import { NAVIGATION_ITEMS } from "../../constants/navigation";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import Logo from "../../assets/logo_transparent.png";
import { MobileMenu } from "./MobileMenu";
import { useAppStore } from "../../store";
import { selectCurrentUserRecord } from "../../store/common/userRecord/slice";
import { useLoader } from "../../contexts/LoaderContext";

export const PrivateLayout = ({ children, hideSidePanel = false }) => {
  const [checking, setChecking] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [activeKey, setActiveKey] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useRef(null);
  const confirmDialogRef = useRef(null);
  const { showLoader, hideLoader } = useLoader();

  const userRecord = useAppStore(selectCurrentUserRecord);

  // Fast-path: if we already have a current user, avoid prolonged loading
  useEffect(() => {
    const auth = getAuth();
    if (auth.currentUser) {
      setIsAuthed(true);
      setChecking(false);
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsAuthed(!!user);
      setChecking(false);
    });
    return () => unsub();
  }, []);

  // Keep side panel active key in sync with the current path
  useEffect(() => {
    const match = Object.values(NAVIGATION_ITEMS).find(
      (item) => item.path && item.path === location.pathname
    );
    if (match && match.key !== activeKey) {
      setActiveKey(match.key);
    }
  }, [location.pathname]);

  if (checking) {
    return <div className="loading-container">Loading...</div>;
  }

  if (!isAuthed) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Build menu items including logout
  const menuItems = Object.values(NAVIGATION_ITEMS)
    .map((item) => ({
      ...item,
      command:
        item.key === "logout"
          ? async () => {
              confirmDialog({
                message: "Are you sure you want to log out?",
                header: "Confirm Logout",
                icon: "pi pi-arrow-left",
                acceptClassName: "p-button-text",
                acceptLabel: "Logout",
                rejectLabel: "Cancel",
                rejectClassName: "p-button-text",
                accept: async () => {
                  const auth = getAuth();
                  try {
                    await showLoader();

                    await signOut(auth);

                    // Clear all user-related data from localStorage
                    Object.keys(localStorage).forEach((key) => {
                      if (
                        key.startsWith("userRecord_") ||
                        key.startsWith("firebase:")
                      ) {
                        localStorage.removeItem(key);
                      }
                    });

                    // Clear session storage
                    await sessionStorage.clear();

                    // Clear any stored credentials
                    if (
                      navigator.credentials &&
                      navigator.credentials.preventSilentAccess
                    ) {
                      navigator.credentials.preventSilentAccess();
                    }

                    // Sign out from Firebase

                    // Clear indexedDB if used
                    if (window.indexedDB && window.indexedDB.databases) {
                      try {
                        const dbs = await window.indexedDB.databases();
                        await Promise.all(
                          dbs.map((db) => {
                            if (db.name) {
                              return new Promise((resolve, reject) => {
                                const req = window.indexedDB.deleteDatabase(
                                  db.name
                                );
                                req.onsuccess = resolve;
                                req.onerror = reject;
                              });
                            }
                            return Promise.resolve();
                          })
                        );
                      } catch (error) {
                        console.warn("Error cleaning up IndexedDB:", error);
                      }
                    }

                    // Show success message
                    toast.current?.show({
                      severity: "success",
                      summary: "Signed out",
                      detail: "You have been logged out successfully.",
                      life: 3000,
                    });
                  } catch (error) {
                    console.error("Logout error:", error);
                    toast.current?.show({
                      severity: "error",
                      summary: "Error",
                      detail: "Failed to log out. Please try again.",
                      life: 3000,
                    });
                  } finally {
                    // Small delay to let toast display
                    setTimeout(() => {
                      // Force a full page reload to ensure all state is cleared
                      window.location.href = "/login";
                      hideLoader();
                    }, 600);
                  }
                },
              });
            }
          : item.path
          ? () => {
              setActiveKey(item.key);
              navigate(item.path);
            }
          : undefined,
    }))
    .filter(
      (item) => item.key === "logout" || typeof item.command === "function"
    );

  const authEmail = getAuth().currentUser?.email || "";
  const shopName = userRecord?.shopName || "Yes Boss";

  return (
    <div className="private-main-layout">
      <ConfirmDialog ref={confirmDialogRef} />
      <Toast ref={toast} />

      {/* Mobile Top Bar with Logo */}
      <div className="mobile-topbar">
        <button
          className="mobile-topbar-logo"
          aria-label="Open menu"
          onClick={() => setMobileMenuOpen(true)}
        >
          <img src={Logo} alt="Yes Boss" />
        </button>
      </div>

      <div className="private-main-body">
        {!hideSidePanel && (
          <SidePanel
            menuItems={menuItems}
            collapsed={collapsed}
            onCollapse={() => setCollapsed((c) => !c)}
            shopName={shopName}
            activeKey={activeKey}
          />
        )}
        <main className="private-main-content">{children}</main>
      </div>

      {mobileMenuOpen && (
        <MobileMenu
          menuItems={menuItems}
          userEmail={authEmail}
          shopName={shopName}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};
